import { useEffect, useSyncExternalStore } from "react";
import { Logger } from "@cede/utils";

const WS_URL = "wss://ws.bitget.com/v2/ws/public";
const MAX_RECONNECT_ATTEMPTS = 10;
const MAX_RECONNECT_DELAY = 30_000;
const PING_INTERVAL = 25_000; // Bitget requires ping every 30s, send every 25s for safety

// --- Module-level singleton managing a single shared WebSocket connection ---

type SetupFn = (ws: WebSocket) => (() => void) | void;
type MessageListener = (data: unknown) => void;
type ErrorListener = (error: string) => void;
type StatusListener = () => void;

interface Subscriber {
  setup: SetupFn;
  onMessage?: MessageListener;
  onError?: ErrorListener;
  cleanup: (() => void) | null;
}

let ws: WebSocket | null = null;
let isConnecting = false;
let isConnected = false;
let connectionError: string | null = null;
let reconnectAttempts = 0;
let reconnectTimeout: NodeJS.Timeout | undefined;
let disconnectTimeout: NodeJS.Timeout | undefined;
let pingInterval: NodeJS.Timeout | undefined;

const subscribers = new Map<symbol, Subscriber>();
const statusListeners = new Set<StatusListener>();

function notifyStatusListeners() {
  statusListeners.forEach((listener) => listener());
}

function runSetup(subscriber: Subscriber) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  subscriber.cleanup?.();
  const cleanup = subscriber.setup(ws);
  subscriber.cleanup = cleanup ?? null;
}

function runAllSetups() {
  subscribers.forEach((subscriber) => runSetup(subscriber));
}

function cleanupAllSubscribers() {
  subscribers.forEach((subscriber) => {
    subscriber.cleanup?.();
    subscriber.cleanup = null;
  });
}

function startPing() {
  stopPing();
  pingInterval = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send("ping");
    }
  }, PING_INTERVAL);
}

function stopPing() {
  if (pingInterval) {
    clearInterval(pingInterval);
    pingInterval = undefined;
  }
}

function disconnect() {
  cleanupAllSubscribers();
  stopPing();

  if (disconnectTimeout) {
    clearTimeout(disconnectTimeout);
    disconnectTimeout = undefined;
  }

  if (ws) {
    ws.close();
    ws = null;
  }

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = undefined;
  }

  isConnecting = false;
  isConnected = false;
  reconnectAttempts = 0;
  notifyStatusListeners();
}

function connect() {
  if (isConnecting || ws || subscribers.size === 0) return;

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = undefined;
  }

  isConnecting = true;
  connectionError = null;
  notifyStatusListeners();

  try {
    const socket = new WebSocket(WS_URL);

    socket.onopen = () => {
      isConnected = true;
      isConnecting = false;
      reconnectAttempts = 0;

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      notifyStatusListeners();
      startPing();
      runAllSetups();
    };

    socket.onmessage = (event) => {
      // Bitget responds "pong" to our "ping" - ignore it
      if (event.data === "pong") return;

      try {
        const msg = JSON.parse(event.data);

        subscribers.forEach((subscriber) => {
          subscriber.onMessage?.(msg);
        });
      } catch {
        // Ignore parse errors for non-JSON messages
      }
    };

    socket.onerror = () => {
      if (socket !== ws) return;

      isConnecting = false;
      isConnected = false;
      const errorMsg = "WebSocket connection error";
      connectionError = errorMsg;
      notifyStatusListeners();
      subscribers.forEach((subscriber) => subscriber.onError?.(errorMsg));
    };

    socket.onclose = () => {
      if (socket !== ws) return;

      isConnecting = false;
      isConnected = false;
      ws = null;
      cleanupAllSubscribers();
      stopPing();
      notifyStatusListeners();

      if (subscribers.size > 0 && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY);
        reconnectAttempts++;

        Logger.log(
          `[bitgetWS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
          Logger.types.INFO,
        );

        reconnectTimeout = setTimeout(() => {
          connect();
        }, delay);
      } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        const errorMsg = "Failed to reconnect after multiple attempts";
        Logger.error("[bitgetWS] Max reconnection attempts reached, giving up");
        connectionError = errorMsg;
        notifyStatusListeners();
        subscribers.forEach((subscriber) => subscriber.onError?.(errorMsg));
      }
    };

    ws = socket;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Unknown error";
    Logger.error("[bitgetWS] Failed to create WebSocket connection:", errorMessage, err);
    isConnecting = false;
    const errorMsg = "Failed to create WebSocket connection";
    connectionError = errorMsg;
    notifyStatusListeners();
    subscribers.forEach((subscriber) => subscriber.onError?.(errorMsg));
  }
}

function subscribe(subscriber: Subscriber): symbol {
  if (disconnectTimeout) {
    clearTimeout(disconnectTimeout);
    disconnectTimeout = undefined;
  }

  const id = Symbol();
  subscribers.set(id, subscriber);

  if (isConnected && ws) {
    runSetup(subscriber);
  } else if (subscribers.size === 1) {
    connect();
  }

  return id;
}

function unsubscribe(id: symbol) {
  const subscriber = subscribers.get(id);
  if (subscriber) {
    subscriber.cleanup?.();
    subscriber.cleanup = null;
    subscribers.delete(id);
  }

  if (subscribers.size === 0) {
    if (disconnectTimeout) clearTimeout(disconnectTimeout);
    disconnectTimeout = setTimeout(() => {
      disconnectTimeout = undefined;
      if (subscribers.size === 0) {
        disconnect();
      }
    }, 0);
  }
}

function getSnapshot() {
  return { isConnected, error: connectionError };
}

let cachedSnapshot = getSnapshot();

function getSnapshotStable() {
  const next = getSnapshot();
  if (next.isConnected !== cachedSnapshot.isConnected || next.error !== cachedSnapshot.error) {
    cachedSnapshot = next;
  }
  return cachedSnapshot;
}

function subscribeToStatus(listener: StatusListener) {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

let visibilityHandlerRegistered = false;

function registerVisibilityHandler() {
  if (visibilityHandlerRegistered || typeof window === "undefined") return;
  visibilityHandlerRegistered = true;

  window.addEventListener("visibilitychange", () => {
    if (subscribers.size === 0) return;

    if (document.visibilityState === "hidden") {
      disconnect();
    } else {
      connect();
    }
  });
}

// --- React hook wrapping the singleton ---

interface UseBitgetWsConnectionProps {
  setup: SetupFn;
  onMessage?: MessageListener;
  enabled?: boolean;
  onError?: ErrorListener;
}

interface UseBitgetWsConnectionResult {
  isConnected: boolean;
  error: string | null;
}

const serverSnapshot = { isConnected: false, error: null };

/**
 * Shared Bitget WebSocket connection hook.
 *
 * All hook instances share a single module-level WebSocket connection.
 * The connection opens when the first consumer mounts and closes when the last unmounts.
 * Ping/pong keepalive, reconnection, and visibility handling are managed centrally.
 */
export const useBitgetWsConnection = ({
  setup,
  onMessage,
  enabled = true,
  onError,
}: UseBitgetWsConnectionProps): UseBitgetWsConnectionResult => {
  const { isConnected: connected, error } = useSyncExternalStore(
    subscribeToStatus,
    getSnapshotStable,
    () => serverSnapshot,
  );

  useEffect(() => {
    if (!enabled) return;

    registerVisibilityHandler();

    const id = subscribe({
      setup,
      onMessage,
      onError,
      cleanup: null,
    });

    return () => {
      unsubscribe(id);
    };
  }, [enabled, setup, onMessage, onError]);

  return {
    isConnected: enabled && connected,
    error: enabled ? error : null,
  };
};
